import { createInertiaApp } from "@inertiajs/react"
import { type ReactNode, StrictMode, createElement } from "react"
import { createRoot } from "react-dom/client"

import { initializeTheme } from "@/hooks/use-appearance"
import PersistentLayout from "@/layouts/persistent-layout"

// Temporary type definition, until @inertiajs/react provides one
interface ResolvedComponent {
  default: ReactNode & { layout?: (page: ReactNode) => ReactNode }
}

const appName = import.meta.env.VITE_APP_NAME ?? "Kasi Admin"

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => (title ? `${title} - ${appName}` : appName),

  resolve: (name) => {
    // Use lazy loading for code splitting - pages load on demand
    // This makes navigation instant as only the necessary code is loaded
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.tsx")
    const pagePath = `../pages/${name}.tsx`

    const pageLoader = pages[pagePath]

    if (!pageLoader) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
      // Return a rejected promise to handle the error gracefully
      return Promise.reject(new Error(`Page not found: ${name}.tsx`))
    }

    // Dynamically import the page component
    return pageLoader().then((module) => {
      const page = module

      if (!page?.default) {
        throw new Error(
          `Page component ${name}.tsx does not have a default export`,
        )
      }

      // To use a default layout, import the Layout component
      // and use the following line.
      // see https://inertia-rails.dev/guide/pages#default-layouts
      //
      page.default.layout ??= (page) =>
        createElement(PersistentLayout, null, page)

      return page
    })
  },

  setup({ el, App, props }) {
    // Uncomment the following to enable SSR hydration:
    // if (el.hasChildNodes()) {
    //   hydrateRoot(el, <App {...props} />)
    //   return
    // }
    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>,
    )
  },

  defaults: {
    future: {
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: "brackets" }
    },
    // Enable prefetching by default for faster navigation
    prefetch: {
      hoverDelay: 50, // Reduced from default 75ms for faster prefetch
    },
  },

  progress: {
    color: "#4B5563",
    // Show progress bar only for non-deferred loads
    // Deferred props load silently in the background
    delay: 100, // Small delay before showing progress bar
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})

// This will set light / dark mode on load...
initializeTheme()
